import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useFeatures } from "@/context/FeaturesContext";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import { useBranding, BrandLogo } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import AIHealthBanner from "@/components/AIHealthBanner";
import CommandMenu from "@/components/CommandMenu";
import AIQueueWidget from "@/components/AIQueueWidget";
import FeedbackWidget from "@/components/FeedbackWidget";
import NotificationBell from "@/components/NotificationBell";
import GlobalSearch from "@/components/GlobalSearch";
import SmartRemindersBell from "@/components/SmartRemindersBell";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Briefcase, Users, LayoutDashboard, Building2, Shield, ShieldAlert, LogOut, User, Bookmark, Trophy, Calendar, GitBranch, CreditCard, Menu, BarChart3, Handshake, Layout, Sparkles, MessagesSquare, Activity, ChevronDown, GitMerge, Palette, LifeBuoy, Headphones, Mail, Inbox, Command, UserCheck, Wallet, TrendingUp, ListTodo, Layers, SlidersHorizontal, KeyRound, Bell, BellOff, PenLine } from "lucide-react";

/**
 * Navigation taxonomy (Phase 72 — icon-only + rich tooltips)
 * ─────────────────────────────────────────────────────────
 * Top-level (always icons at lg+): Dashboard, Feed, Jobs, Candidates,
 *   Pipeline, Calendar + three grouped dropdowns (Insights, Sourcing,
 *   Workspace). Every trigger carries a rich tooltip with the feature name
 *   and one-line description so recruiters can learn the icons quickly.
 * Right rail: Global search · Inbox · Reminders · Notifications · Avatar
 *   dropdown (includes Feedback + Quick menu here to free up top-bar space).
 * Below lg (mobile / tablet): hamburger sheet with the full nav in text form.
 */
const TOP = [
    { kind: "link", to: "/dashboard", label: "Dashboard", desc: "Your daily workspace — KPIs, live feed and quick actions.", icon: LayoutDashboard, roles: ["employer","recruiter","admin"], feature: "nav.dashboard" },
    { kind: "link", to: "/my-week", label: "My week", desc: "Your assigned jobs by urgency with next best actions — the daily to-do list.", icon: ListTodo, roles: ["employer","recruiter"] },
    { kind: "link", to: "/feed", label: "Feed", desc: "Real-time company activity — mentions, votes, new applicants.", icon: Sparkles, roles: ["employer","recruiter"], feature: "nav.feed" },
    { kind: "link", to: "/jobs", label: "Jobs", desc: "Every requisition — publish, edit, close, promote.", icon: Briefcase, roles: ["employer","recruiter"], feature: "nav.jobs" },
    { kind: "link", to: "/candidates", label: "Candidates", desc: "Your entire candidate database — search, filter, bulk actions.", icon: Users, roles: ["employer","recruiter"], feature: "nav.candidates" },
    { kind: "link", to: "/pipeline", label: "Pipeline", desc: "Cross-job pipeline board — drag candidates between stages.", icon: GitBranch, roles: ["employer","recruiter"] },
    { kind: "link", to: "/calendar", label: "Calendar", desc: "Interviews and follow-ups on a single calendar view.", icon: Calendar, roles: ["employer","recruiter"], feature: "nav.interviews" },
    { kind: "link", to: "/interviewer", label: "Interview mode", desc: "Focus-mode list of candidates assigned to you for interviews — same view interviewers use.", icon: Calendar, roles: ["employer","recruiter","admin"], feature: "nav.interviews" },
    { kind: "link", to: "/docs-pending", label: "Docs pending", desc: "Candidates at a gate stage still missing joining/relieving letters.", icon: ShieldAlert, roles: ["employer","recruiter","admin"] },
    {
        kind: "group", id: "insights", label: "Insights", desc: "Reports, hires ledger, cost tracker and leaderboards.",
        icon: BarChart3,
        roles: ["employer","recruiter"],
        items: [
            { to: "/reports", label: "Results & ROI", icon: TrendingUp, feature: "nav.reports" },
            { to: "/reports/dsr", label: "Daily Status Report", icon: TrendingUp, badge: "NEW", feature: "nav.reports.dsr" },
            { to: "/hires", label: "Hires ledger", icon: UserCheck, feature: "nav.hires" },
            { to: "/interviews/history", label: "Interview history", icon: Calendar, feature: "nav.interviews" },
            { to: "/costs", label: "Cost tracker", icon: Wallet },
            { to: "/analytics", label: "Pipeline analytics", icon: BarChart3 },
            { to: "/job-analytics", label: "Job-board analytics", icon: Activity },
            { to: "/team", label: "Team leaderboard", icon: Trophy },
            { to: "/offers/inbox", label: "Offer approvals", icon: Handshake, roles: ["employer","admin"], feature: "nav.offers" },
        ],
    },
    {
        kind: "group", id: "sourcing", label: "Sourcing", desc: "AI search, hiring lists, embed boards and dedupe hygiene.",
        icon: Sparkles,
        roles: ["employer","recruiter","admin"],
        items: [
            { to: "/talent-search", label: "AI Search", icon: MessagesSquare, roles: ["employer","recruiter"], feature: "nav.talent_search" },
            { to: "/lists", label: "Hiring lists", icon: Bookmark, roles: ["employer","recruiter"] },
            { to: "/embed-boards", label: "Embed boards", icon: Layout, roles: ["employer","recruiter"], feature: "jobs.action.embed_board" },
            { to: "/seeds", label: "JD Seeds", icon: Sparkles, roles: ["employer","recruiter"] },
            { to: "/skill-center", label: "Skill Center", icon: Layers, feature: "nav.skill_center" },
            { to: "/candidate-duplicates", label: "Hygiene · Dedupe", icon: GitMerge, roles: ["employer","recruiter"] },
        ],
    },
    {
        kind: "group", id: "settings", label: "Workspace", desc: "Clients, branding, billing, templates.",
        icon: Building2,
        roles: ["employer","recruiter","admin"],
        items: [
            { to: "/clients", label: "Clients", icon: Handshake, roles: ["employer","recruiter"], feature: "nav.clients" },
            { to: "/branding", label: "Branding", icon: Palette, roles: ["employer","admin"], feature: "nav.branding" },
            { to: "/campaigns", label: "Priority Roles", icon: Sparkles, roles: ["employer","recruiter","admin"], badge: "NEW", feature: "nav.campaigns" },
            { to: "/spotlight-roi", label: "Spotlight ROI", icon: TrendingUp, roles: ["employer","admin"] },
            { to: "/spotlight", label: "Public Spotlight Directory", icon: Sparkles, roles: ["employer","recruiter","admin"], feature: "nav.spotlight_directory" },
            { to: "/landing-page", label: "Landing page", icon: Layout, roles: ["employer","admin"] },
            { to: "/company", label: "Company", icon: Building2, roles: ["employer","recruiter"], feature: "nav.settings" },
            { to: "/company/offer-settings", label: "Offer structure", icon: CreditCard, roles: ["employer","admin"] },
            { to: "/company/screening-preferences", label: "Screening preferences", icon: SlidersHorizontal, roles: ["employer","admin"] },
            { to: "/company/doc-freshness", label: "Doc freshness alerts", icon: ShieldAlert, roles: ["employer","admin"], badge: "NEW" },
            { to: "/company/interviewer-settings", label: "Interviewer visibility", icon: ShieldAlert, roles: ["employer","admin"], badge: "NEW" },
            { to: "/settings/email", label: "Email settings", icon: Mail, roles: ["employer","recruiter","admin"], badge: "NEW" },
            { to: "/company/email-templates", label: "Email templates", icon: Mail, roles: ["employer","recruiter"] },
            { to: "/company/email-signature", label: "Email signature", icon: PenLine, roles: ["employer","recruiter"], badge: "NEW" },
            { to: "/billing", label: "Billing", icon: CreditCard, roles: ["employer"] },
            { to: "/learn", label: "Learn (Help center)", icon: Sparkles, roles: ["employer","recruiter"], badge: "NEW", feature: "nav.learn" },
        ],
    },
    // Admin console — dedicated top-level dropdown for SuperAdmins only.
    // Keeps admin-only tools out of the employer/recruiter workspace menu
    // and gives them room to grow as more employer-management features
    // ship (impersonation, MRR, feature management, subscriptions).
    {
        kind: "group", id: "admin", label: "Admin", desc: "Employer management, subscriptions, revenue, support.",
        icon: Shield,
        roles: ["admin"],
        items: [
            { to: "/admin", label: "Admin console", icon: LayoutDashboard, roles: ["admin"] },
            { to: "/admin/employers", label: "Employers", icon: Building2, roles: ["admin"] },
            { to: "/admin/features", label: "Feature Management", icon: Sparkles, roles: ["admin"] },
            { to: "/admin/subscriptions", label: "Subscriptions & MRR", icon: CreditCard, roles: ["admin"] },
            { to: "/admin/subscription-plans", label: "Plans & pricing", icon: Layers, roles: ["admin"] },
            { to: "/admin/campaign-codes", label: "Campaign codes", icon: Sparkles, roles: ["admin"] },
            { to: "/admin/integration-keys", label: "Integration keys", icon: KeyRound, roles: ["admin"] },
            { to: "/admin/seller-billing", label: "Seller billing entity", icon: Building2, roles: ["admin"] },
            { to: "/admin/spotlight-funnel", label: "Spotlight funnel", icon: TrendingUp, roles: ["admin"] },
            { to: "/admin/landing-content", label: "Landing content", icon: Layout, roles: ["admin"] },
            { to: "/admin/helpdesk", label: "Helpdesk", icon: Headphones, roles: ["admin"] },
        ],
    },
];

function filterTop(role, isFeatureEnabled) {
    // isFeatureEnabled: (id?: string) => boolean — returns true when no id
    // is set (nav items without a mapped feature). Passed in from the
    // component so we read the FeaturesContext once, not per iteration.
    const featOk = (item) => isFeatureEnabled(item.feature);
    return TOP
        .filter((g) => (!g.roles || g.roles.includes(role)) && featOk(g))
        .map((g) => {
            if (g.kind === "group") {
                const sub = (g.items || []).filter((i) => (!i.roles || i.roles.includes(role)) && featOk(i));
                return { ...g, items: sub };
            }
            return g;
        })
        .filter((g) => g.kind !== "group" || g.items.length > 0);
}

/** Reusable rich-tooltip wrapper — name (bold) + one-line description. */
function NavTip({ children, label, desc, side = "bottom" }) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>{children}</TooltipTrigger>
            <TooltipContent side={side}>
                <div className="font-semibold text-[12px] leading-tight">{label}</div>
                {desc && <div className="text-[11px] text-background/70 mt-0.5 leading-snug">{desc}</div>}
            </TooltipContent>
        </Tooltip>
    );
}

export default function AppShell({ children }) {
    const { user, logout } = useAuth();
    const { features, impersonating, impersonation, setImpersonation } = useFeatures();
    const branding = useBranding();
    const nav = useNavigate();
    const loc = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mentionCount, setMentionCount] = useState(0);
    const [unreadMessages, setUnreadMessages] = useState(0);

    // O(1) feature check — reads the resolved map that was loaded once at
    // login (see FeaturesContext). Unknown/blank ids default to true so a
    // stray typo never accidentally hides a nav item.
    const isFeatureEnabled = (id) => {
        if (!id) return true;
        if (Object.prototype.hasOwnProperty.call(features, id)) return !!features[id];
        return true;
    };
    const items = filterTop(user?.role || "employer", isFeatureEnabled);

    const userRole = user?.role;
    const userId = user?.id;
    useEffect(() => {
        if (!userId || !["employer", "recruiter"].includes(userRole)) return () => {};
        let alive = true;
        const load = async () => {
            try {
                const { data } = await api.get("/feed/counts");
                if (alive) setMentionCount(data?.my_mentions || 0);
            } catch { /* silent */ }
        };
        load();
        const t = setInterval(load, 60000);
        window.addEventListener("pw-feed-refresh", load);
        return () => { alive = false; clearInterval(t); window.removeEventListener("pw-feed-refresh", load); };
    }, [userId, userRole]);

    // Phase 155 — poll unread message count for the top-bar Messages icon badge.
    // Also listens for a window event so dialogs can refresh instantly after
    // marking a thread read.
    useEffect(() => {
        if (!userId || !["employer", "recruiter", "seeker"].includes(userRole)) return () => {};
        let alive = true;
        const load = async () => {
            try {
                const { data } = await api.get("/messages/unread-count");
                if (alive) setUnreadMessages(data?.total || 0);
            } catch { /* silent */ }
        };
        load();
        const t = setInterval(load, 45000);
        window.addEventListener("pw-messages-refresh", load);
        return () => { alive = false; clearInterval(t); window.removeEventListener("pw-messages-refresh", load); };
    }, [userId, userRole]);

    // Phase 155c — Server-Sent Events live push: a new candidate reply
    // dispatches `pw-messages-refresh` so the top-bar badge + popover
    // update the instant the message hits the server. EventSource uses
    // the httpOnly auth cookie via `withCredentials:true`.
    //
    // Phase 155d — the same event now ALSO:
    //   • pops a lightweight sonner toast with the candidate's name and a
    //     one-tap "Open" action that routes to the thread, UNLESS the user
    //     is already looking at that thread's page
    //   • if the user has granted `Notification.permission === 'granted'`,
    //     raises an OS-level notification (same suppression rule)
    //   • is deliberately silent when the user's OWN role matches the
    //     sender_role — we don't want to notify a recruiter about their
    //     own reply bouncing back through the fan-out.
    useEffect(() => {
        if (!userId || !["employer", "recruiter", "seeker"].includes(userRole)) return () => {};
        const base = process.env.REACT_APP_BACKEND_URL;
        if (!base) return () => {};
        let es;
        let closed = false;
        let backoff = 2000;
        const open = () => {
            if (closed) return;
            try {
                es = new EventSource(`${base}/api/messages/stream`, { withCredentials: true });
                es.addEventListener("ready", () => { backoff = 2000; });
                es.addEventListener("message", (ev) => {
                    try {
                        const payload = JSON.parse(ev.data || "{}");
                        if (payload.type === "email.bounced") {
                            // Phase 157 — surface delivery failures live. The
                            // recruiter who triggered the send likely has the
                            // candidate open; we toast with a jump-to-candidate
                            // action instead of touching the Messages channel.
                            notifyBounce(payload, nav);
                            return;
                        }
                        window.dispatchEvent(new CustomEvent("pw-messages-refresh", { detail: payload }));
                        notifyIfInbound(payload, userRole, nav);
                    } catch { /* ignore malformed */ }
                });
                es.onerror = () => {
                    try { es?.close(); } catch { /* ignore */ }
                    if (!closed) setTimeout(open, backoff);
                    backoff = Math.min(backoff * 1.6, 30000);
                };
            } catch { /* ignore — fall back to the 45s poll */ }
        };
        open();
        return () => { closed = true; try { es?.close(); } catch { /* ignore */ } };
    }, [userId, userRole, nav]);

    const handleLogout = async () => {
        await logout();
        nav("/login");
    };

    const initials = (user?.name || user?.email || "?")
        .split(/\s|@/).filter(Boolean).map((s) => s[0]).slice(0, 2).join("").toUpperCase();

    return (
        <TooltipProvider delayDuration={250} skipDelayDuration={100}>
            <div className="min-h-screen bg-background flex flex-col">
                <SubscriptionBanner />
                {/* Impersonation banner — sticky above everything so the admin
                 *  never forgets they're viewing the app as another employer.
                 *  All requests are automatically read-only (backend blocks
                 *  writes via the impersonation_read_only_guard middleware). */}
                {impersonating && (
                    <div className="bg-amber-500 text-black px-4 py-1.5 flex items-center gap-3 justify-center sticky top-0 z-40" data-testid="impersonation-banner">
                        <span className="text-[10px] font-mono uppercase tracking-widest font-bold">Read-only preview</span>
                        <span className="text-xs">Impersonating <b>{impersonation?.company_name || impersonation?.company_id}</b> — writes blocked.</span>
                        <button
                            type="button"
                            onClick={async () => {
                                await setImpersonation(null);
                                // Navigate back to the admin console so the
                                // admin lands somewhere they can actually
                                // interact with once preview mode ends.
                                nav("/admin");
                            }}
                            className="ml-auto text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 bg-black text-amber-500 hover:bg-black/90"
                            data-testid="impersonation-exit"
                        >
                            Exit preview
                        </button>
                    </div>
                )}
                <header className="border-b border-border bg-white sticky top-0 z-30">
                    <div className="flex items-center h-14 lg:h-16 px-3 sm:px-4 gap-2 lg:gap-3 max-w-full">
                        {/* Mobile hamburger */}
                        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                            <SheetTrigger asChild>
                                <button
                                    type="button"
                                    className="lg:hidden p-2 -ml-2 hover:bg-secondary"
                                    data-testid="app-mobile-menu-btn"
                                    aria-label="Open menu"
                                >
                                    <Menu className="w-5 h-5" />
                                </button>
                            </SheetTrigger>
                            <SheetContent side="left" className="rounded-none w-72 p-0 flex flex-col">
                                <div className="border-b border-border px-5 py-4 flex items-center gap-2">
                                    <BrandLogo size={26} />
                                    <span className="font-display font-black tracking-tight text-base">{branding.short_name}</span>
                                </div>
                                <nav className="flex-1 overflow-y-auto py-2">
                                    {items.map((n) => {
                                        if (n.kind === "group") {
                                            return (
                                                <div key={n.id} className="py-1">
                                                    <div className="px-5 pt-2 pb-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{n.label}</div>
                                                    {n.items.map((sub) => {
                                                        const SubIcon = sub.icon;
                                                        const active = loc.pathname.startsWith(sub.to);
                                                        return (
                                                            <Link
                                                                key={sub.to}
                                                                to={sub.to}
                                                                onClick={() => setMobileOpen(false)}
                                                                data-testid={`mobile-nav-${sub.label.toLowerCase().replace(/\W+/g, "-")}`}
                                                                className={`flex items-center gap-3 px-5 py-2.5 text-sm font-medium ${
                                                                    active ? "bg-blue-50 text-ikb border-l-4 border-ikb" : "text-foreground hover:bg-secondary border-l-4 border-transparent"
                                                                }`}
                                                            >
                                                                <SubIcon className="w-4 h-4" />
                                                                {sub.label}
                                                            </Link>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        }
                                        const active = loc.pathname.startsWith(n.to);
                                        const Icon = n.icon;
                                        return (
                                            <Link
                                                key={n.to}
                                                to={n.to}
                                                onClick={() => setMobileOpen(false)}
                                                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\W+/g, "-")}`}
                                                className={`flex items-center gap-3 px-5 py-3 text-sm font-medium ${
                                                    active ? "bg-blue-50 text-ikb border-l-4 border-ikb" : "text-foreground hover:bg-secondary border-l-4 border-transparent"
                                                }`}
                                            >
                                                <Icon className="w-4 h-4" />
                                                {n.label}
                                            </Link>
                                        );
                                    })}
                                </nav>
                                {user && (
                                    <div className="border-t border-border p-4 space-y-2">
                                        <div className="text-xs font-mono text-muted-foreground truncate">{user.email}</div>
                                        <button
                                            onClick={() => { setMobileOpen(false); handleLogout(); }}
                                            className="w-full text-left text-sm flex items-center gap-2 py-2 hover:text-ikb"
                                            data-testid="mobile-menu-logout"
                                        >
                                            <LogOut className="w-4 h-4" /> Sign out
                                        </button>
                                    </div>
                                )}
                            </SheetContent>
                        </Sheet>

                        {/* Brand — logo always, wordmark only at 2xl+ */}
                        <Link to="/dashboard" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
                            <BrandLogo size={28} />
                            <span className="font-display font-black tracking-tight text-base hidden 2xl:inline">{branding.short_name}</span>
                        </Link>

                        {/* Icon-only nav at lg+ — rich tooltip on hover */}
                        <nav className="hidden lg:flex items-center gap-0.5" data-testid="app-nav">
                            {items.map((n) => {
                                if (n.kind === "group") {
                                    const Icon = n.icon;
                                    const subActive = (n.items || []).some((i) => loc.pathname.startsWith(i.to));
                                    return (
                                        <DropdownMenu key={n.id}>
                                            <NavTip label={n.label} desc={n.desc}>
                                                <DropdownMenuTrigger asChild>
                                                    <button
                                                        type="button"
                                                        data-testid={`nav-group-${n.id}`}
                                                        aria-label={n.label}
                                                        className={`p-2 border-b-2 transition-colors ${
                                                            subActive ? "text-ikb border-ikb -mb-px" : "text-muted-foreground hover:text-foreground border-transparent"
                                                        }`}
                                                    >
                                                        <Icon className="w-[18px] h-[18px]" />
                                                    </button>
                                                </DropdownMenuTrigger>
                                            </NavTip>
                                            <DropdownMenuContent align="start" className="w-56 rounded-none">
                                                <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{n.label}</DropdownMenuLabel>
                                                <DropdownMenuSeparator />
                                                {n.items.map((sub) => {
                                                    const SubIcon = sub.icon;
                                                    const active = loc.pathname.startsWith(sub.to);
                                                    return (
                                                        <DropdownMenuItem key={sub.to} asChild>
                                                            <Link
                                                                to={sub.to}
                                                                data-testid={`nav-sub-${sub.to.replace(/\//g, "-")}`}
                                                                className={`flex items-center gap-2 ${active ? "text-ikb font-medium" : ""}`}
                                                            >
                                                                <SubIcon className="w-4 h-4" />
                                                                {sub.label}
                                                            </Link>
                                                        </DropdownMenuItem>
                                                    );
                                                })}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    );
                                }
                                const Icon = n.icon;
                                const active = loc.pathname.startsWith(n.to);
                                return (
                                    <NavTip key={n.to} label={n.label} desc={n.desc}>
                                        <Link
                                            to={n.to}
                                            data-testid={`nav-${n.label.toLowerCase().replace(/\W+/g, "-")}`}
                                            aria-label={n.label}
                                            className={`p-2 border-b-2 transition-colors relative ${
                                                active ? "text-ikb border-ikb -mb-px" : "text-muted-foreground hover:text-foreground border-transparent"
                                            }`}
                                        >
                                            <Icon className="w-[18px] h-[18px]" />
                                            {n.to === "/feed" && mentionCount > 0 && (
                                                <span
                                                    className="absolute -top-0.5 -right-1 min-w-[14px] h-[14px] px-1 bg-rose-500 text-white text-[9px] font-mono font-bold uppercase tracking-widest inline-flex items-center justify-center rounded-none"
                                                    data-testid="feed-nav-mention-badge"
                                                >
                                                    {mentionCount > 9 ? "9+" : mentionCount}
                                                </span>
                                            )}
                                        </Link>
                                    </NavTip>
                                );
                            })}
                        </nav>

                        {/* Right rail — always icon-only, tooltips on all */}
                        <div className="ml-auto flex items-center gap-1 sm:gap-1.5 shrink-0">
                            {user && (user.role === "employer" || user.role === "recruiter") && (
                                <div className="hidden md:block w-44 lg:w-52 xl:w-64">
                                    <GlobalSearch />
                                </div>
                            )}
                            {user && (user.role === "employer" || user.role === "recruiter") && (
                                <UnreadMessagesPopover
                                    unreadCount={unreadMessages}
                                    active={loc.pathname.startsWith("/messages")}
                                />
                            )}
                            {user && (user.role === "employer" || user.role === "recruiter") && (
                                <NavTip label="Inbox" desc="Unmatched SMS · WhatsApp · Email inbound messages.">
                                    <Link
                                        to="/inbox"
                                        data-testid="nav-inbox"
                                        aria-label="Inbox"
                                        className={`p-1.5 border border-transparent hover:border-border transition-colors inline-flex ${loc.pathname.startsWith("/inbox") ? "text-ikb" : "text-foreground/80"}`}
                                    >
                                        <Inbox className="w-4 h-4" />
                                    </Link>
                                </NavTip>
                            )}
                            <SmartRemindersBell />
                            <NotificationBell />

                            {/* Feedback + Quick menu tucked into a compact ⌘K trigger */}
                            <NavTip label="Quick menu" desc="Press ⌘K anywhere — search, jump between features, run actions.">
                                <button
                                    type="button"
                                    onClick={() => window.dispatchEvent(new CustomEvent("pw-open-power-menu"))}
                                    className="hidden md:inline-flex items-center gap-1 px-1.5 py-1 border border-transparent hover:border-border text-foreground/80 hover:text-foreground transition-colors"
                                    aria-label="Quick menu"
                                    data-testid="power-menu-btn"
                                >
                                    <Command className="w-3.5 h-3.5" />
                                    <span className="text-[9px] font-mono uppercase tracking-widest">K</span>
                                </button>
                            </NavTip>

                            <div className="hidden md:inline-flex">
                                <FeedbackWidget />
                            </div>

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        data-testid="user-menu-btn"
                                        aria-label="Account menu"
                                        className="flex items-center gap-2 px-1 py-1 hover:bg-secondary transition-colors"
                                    >
                                        <div className="w-8 h-8 bg-foreground text-background flex items-center justify-center font-mono text-xs font-bold">
                                            {initials}
                                        </div>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="rounded-none w-64">
                                    <DropdownMenuLabel className="font-mono text-xs">
                                        <div className="truncate">{user?.name}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">{user?.role}</div>
                                        <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{user?.email}</div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => nav("/company")} data-testid="menu-company">
                                        <User className="w-4 h-4 mr-2" /> My company
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => nav("/tickets")} data-testid="menu-tickets">
                                        <LifeBuoy className="w-4 h-4 mr-2" /> My tickets
                                    </DropdownMenuItem>
                                    {user?.role === "admin" && (
                                        <DropdownMenuItem onClick={() => nav("/admin/helpdesk")} data-testid="menu-helpdesk">
                                            <Headphones className="w-4 h-4 mr-2" /> Helpdesk
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                                        <LogOut className="w-4 h-4 mr-2" /> Sign out
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </header>
                <AIHealthBanner />
                <main className="flex-1">{children}</main>
                <CommandMenu />
                <AIQueueWidget />
            </div>
        </TooltipProvider>
    );
}


/** Phase 155b — top-bar unread messages popover.
 *  Shows the newest ≤6 threads where the recruiter has at least one
 *  unread seeker reply. Clicking a row jumps straight into the
 *  conversation (`/seeker/messages/:tid` → recruiter dialog landing).
 *  "Open all" goes to the full `/messages` surface.
 */

// ─────────── Phase 155d — toast + OS notification on live SSE push ───────────

/** Returns true when we should SUPPRESS a notification because the user is
 *  already looking at that specific thread. Covers both /messages/:tid
 *  (recruiter) and /seeker/messages/:tid (candidate portal).
 */
function _isViewingThread(threadId) {
    if (!threadId) return false;
    try {
        const p = window.location.pathname || "";
        return p === `/messages/${threadId}` || p === `/seeker/messages/${threadId}`;
    } catch { return false; }
}

/** Returns true when the fan-out came from the viewer's OWN role, which
 *  means the event is just our own reply bouncing back and should be silent.
 */
function _isOwnMessage(sender, viewerRole) {
    if (!sender || !viewerRole) return false;
    if (viewerRole === "seeker" && sender === "seeker") return true;
    if (["employer", "recruiter"].includes(viewerRole) && ["employer", "recruiter"].includes(sender)) return true;
    return false;
}

const _brNotif = {
    /** Pulls localStorage preference. 'off' = user disabled; 'on' = user opted in. */
    pref: () => {
        try { return localStorage.getItem("pw.notif.msg") || ""; } catch { return ""; }
    },
    setPref: (v) => {
        try { localStorage.setItem("pw.notif.msg", v); } catch { /* ignore */ }
    },
};

function notifyBounce(payload, nav) {
    if (!payload || payload.type !== "email.bounced") return;
    const subject = payload.subject || "last email";
    const label = payload.status === "complained"
        ? "Candidate marked email as spam"
        : "Candidate email bounced";
    toast.error(label, {
        description: `"${subject}" was returned as undeliverable. The candidate address is likely dead.`,
        duration: 9000,
        action: payload.candidate_id ? {
            label: "Open candidate",
            onClick: () => {
                try { nav(`/candidates/${payload.candidate_id}`); }
                catch { window.location.href = `/candidates/${payload.candidate_id}`; }
            },
        } : undefined,
    });
    // OS-level alert — same opt-in pref as the inbound message notification.
    try {
        if (_brNotif.pref() !== "on") return;
        if (typeof Notification === "undefined") return;
        if (Notification.permission !== "granted") return;
        if (!document.hidden && document.hasFocus()) return;
        const n = new Notification(label, {
            body: `"${subject}" bounced. Address is likely dead.`,
            tag: `pw-bounce-${payload.candidate_id || "x"}`,
            renotify: true,
        });
        n.onclick = () => {
            try { window.focus(); } catch { /* ignore */ }
            if (payload.candidate_id) {
                try { nav(`/candidates/${payload.candidate_id}`); }
                catch { window.location.href = `/candidates/${payload.candidate_id}`; }
            }
            n.close();
        };
    } catch { /* ignore */ }
}


function notifyIfInbound(payload, viewerRole, nav) {
    if (!payload || payload.type !== "message.new") return;
    if (_isOwnMessage(payload.sender_role, viewerRole)) return;
    if (_isViewingThread(payload.thread_id)) return;
    // Decide the right landing route for a one-tap jump.
    const isSeekerViewer = viewerRole === "seeker";
    const route = isSeekerViewer
        ? `/seeker/messages/${payload.thread_id}`
        : `/messages/${payload.thread_id}`;
    const who = isSeekerViewer
        ? (payload.sender_name || "Recruiter")
        : (payload.candidate_name || payload.sender_name || "Candidate");
    const title = `New reply from ${who}`;
    const preview = (payload.preview || "").trim();
    // Lightweight sonner toast — one-tap "Open" jumps straight to the thread.
    toast(title, {
        description: preview || "Tap Open to view the thread.",
        duration: 7000,
        action: {
            label: "Open",
            onClick: () => { try { nav(route); } catch { window.location.href = route; } },
        },
    });
    // OS-level notification — only when the user has opted in AND permission
    // is still granted. We also skip if the tab is already focused: the toast
    // alone is enough in that case.
    try {
        if (_brNotif.pref() !== "on") return;
        if (typeof Notification === "undefined") return;
        if (Notification.permission !== "granted") return;
        if (!document.hidden && document.hasFocus()) return;
        const n = new Notification(title, {
            body: preview || "You have a new message.",
            tag: `pw-msg-${payload.thread_id}`,
            renotify: true,
        });
        n.onclick = () => {
            try { window.focus(); } catch { /* ignore */ }
            try { nav(route); } catch { window.location.href = route; }
            n.close();
        };
    } catch { /* ignore */ }
}

/** Phase 155d — opt-in control for OS-level browser notifications.
 *  Rendered inside the Messages popover footer so it's discoverable
 *  without stealing header space.
 */
function BrowserNotifToggle() {
    const [state, setState] = useState(() => {
        if (typeof Notification === "undefined") return "unsupported";
        if (Notification.permission === "denied") return "blocked";
        if (Notification.permission === "granted" && _brNotif.pref() === "on") return "on";
        return "off";
    });
    const enable = async () => {
        if (typeof Notification === "undefined") return;
        if (Notification.permission === "granted") {
            _brNotif.setPref("on");
            setState("on");
            toast.success("Browser alerts enabled.");
            return;
        }
        try {
            const result = await Notification.requestPermission();
            if (result === "granted") {
                _brNotif.setPref("on");
                setState("on");
                toast.success("Browser alerts enabled.");
            } else if (result === "denied") {
                setState("blocked");
                toast("Browser alerts blocked.", { description: "Allow notifications in your browser site settings and try again." });
            }
        } catch { /* ignore */ }
    };
    const disable = () => {
        _brNotif.setPref("off");
        setState("off");
        toast("Browser alerts off.");
    };
    if (state === "unsupported") return null;
    if (state === "blocked") {
        return (
            <div className="px-3 py-2 border-t border-border bg-secondary/30 text-[10px] font-mono uppercase tracking-widest text-foreground/60 flex items-center gap-1.5" data-testid="nav-messages-notif-blocked">
                <BellOff className="w-3 h-3" /> Browser alerts blocked — update site settings to enable
            </div>
        );
    }
    if (state === "on") {
        return (
            <button
                type="button"
                onClick={disable}
                className="w-full px-3 py-2 border-t border-border bg-emerald-50 text-emerald-800 text-[11px] font-mono uppercase tracking-widest hover:bg-emerald-100 inline-flex items-center justify-center gap-1.5"
                data-testid="nav-messages-notif-on"
            >
                <Bell className="w-3 h-3" /> Browser alerts on · turn off
            </button>
        );
    }
    return (
        <button
            type="button"
            onClick={enable}
            className="w-full px-3 py-2 border-t border-border bg-white text-[11px] font-mono uppercase tracking-widest hover:bg-secondary inline-flex items-center justify-center gap-1.5"
            data-testid="nav-messages-notif-enable"
            title="Pop a desktop notification when a candidate replies while this tab is in the background."
        >
            <Bell className="w-3 h-3" /> Enable browser alerts
        </button>
    );
}

function UnreadMessagesPopover({ unreadCount, active }) {
    const [open, setOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/messages/threads", { params: { status: "all" } });
            const unread = (data || [])
                .filter((t) => (t.unread_count || 0) > 0)
                .sort((a, b) => (b.last_message_at || "").localeCompare(a.last_message_at || ""))
                .slice(0, 6);
            setRows(unread);
        } catch { /* silent */ }
        finally { setLoading(false); }
    };

    useEffect(() => {
        if (open) load();
    }, [open]);

    // Phase 155c — refresh the open popover whenever the live SSE event
    // or any other mutation fires `pw-messages-refresh`.
    useEffect(() => {
        const handler = () => { if (open) load(); };
        window.addEventListener("pw-messages-refresh", handler);
        return () => window.removeEventListener("pw-messages-refresh", handler);
    }, [open]);

    const fmtWhen = (iso) => {
        if (!iso) return "";
        try {
            const t = new Date(iso);
            const diff = (Date.now() - t.getTime()) / 1000;
            if (diff < 60) return "just now";
            if (diff < 3600) return `${Math.floor(diff / 60)}m`;
            if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
            if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
            return t.toLocaleDateString();
        } catch { return ""; }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <NavTip label="Messages" desc="Recruiter ↔ candidate threads. Snooze paused conversations.">
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        data-testid="nav-messages"
                        aria-label={unreadCount > 0 ? `Messages — ${unreadCount} unread` : "Messages"}
                        className={`relative p-1.5 border border-transparent hover:border-border transition-colors inline-flex ${active ? "text-ikb" : "text-foreground/80"}`}
                    >
                        <Mail className="w-4 h-4" />
                        {unreadCount > 0 && (
                            <span
                                className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 bg-rose-600 text-white text-[9px] font-bold leading-[16px] text-center rounded-full border border-white"
                                data-testid="nav-messages-badge"
                            >
                                {unreadCount > 99 ? "99+" : unreadCount}
                            </span>
                        )}
                    </button>
                </PopoverTrigger>
            </NavTip>
            <PopoverContent align="end" className="w-[320px] p-0 rounded-none" data-testid="nav-messages-popover">
                <div className="px-3 py-2 border-b border-border bg-secondary/40 flex items-center justify-between">
                    <div className="text-[11px] font-mono uppercase tracking-widest font-bold">
                        Unread · {unreadCount}
                    </div>
                    <Link
                        to="/messages"
                        onClick={() => setOpen(false)}
                        className="text-[11px] font-mono uppercase tracking-widest text-ikb hover:underline"
                        data-testid="nav-messages-popover-open-all"
                    >
                        Open all →
                    </Link>
                </div>
                <div className="max-h-[360px] overflow-y-auto" data-testid="nav-messages-popover-list">
                    {loading ? (
                        <div className="px-3 py-6 text-xs text-foreground/60 text-center">Loading…</div>
                    ) : rows.length === 0 ? (
                        <div className="px-3 py-6 text-xs text-foreground/60 text-center">
                            {unreadCount === 0 ? "You're all caught up." : "No unread threads to show."}
                        </div>
                    ) : (
                        <ul className="divide-y divide-border">
                            {rows.map((t) => (
                                <li key={t.id}>
                                    <Link
                                        to={`/messages/${t.id}`}
                                        onClick={() => setOpen(false)}
                                        className="block px-3 py-2.5 hover:bg-secondary/60 transition-colors"
                                        data-testid={`nav-messages-popover-row-${t.id}`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold truncate text-foreground">
                                                    {t.candidate_name || t.candidate_email || "Candidate"}
                                                </div>
                                                <div className="text-[11px] text-foreground/70 truncate">
                                                    {t.subject || "No subject"}
                                                </div>
                                            </div>
                                            <div className="shrink-0 flex items-center gap-1.5">
                                                <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/50">{fmtWhen(t.last_message_at)}</span>
                                                <span className="min-w-[18px] h-[18px] px-1 bg-rose-600 text-white text-[10px] font-bold leading-[18px] text-center rounded-full">
                                                    {t.unread_count > 9 ? "9+" : t.unread_count}
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <BrowserNotifToggle />
            </PopoverContent>
        </Popover>
    );
}
